import { usTimeZones } from '@jlg-website/shared';

// Building an `Intl.DateTimeFormat` is expensive and a zone's name never changes, so each one
// is resolved once for the lifetime of the page instead of on every render.
const labelCache = new Map<string, string>();

function readTimeZoneLabel(timeZone: string): string {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone,
      timeZoneName: 'longGeneric',
    }).formatToParts(new Date());

    return parts.find((part) => part.type === 'timeZoneName')?.value ?? timeZone;
  } catch {
    return timeZone;
  }
}

/**
 * How the visitor reads a zone: `America/Los_Angeles` becomes `Pacific Time`. `longGeneric`
 * drops the standard/daylight distinction, which is noise for someone picking where they live,
 * and covers every IANA name without a table to keep in sync.
 */
export function timeZoneLabel(timeZone: string): string {
  const cached = labelCache.get(timeZone);
  if (cached !== undefined) return cached;

  const label = readTimeZoneLabel(timeZone);
  labelCache.set(timeZone, label);

  return label;
}

const cityOf = (timeZone: string) => timeZone.split('/').at(-1)?.replace(/_/g, ' ') ?? timeZone;

/** `Pacific Time (Los Angeles)` — the region alone repeats across zones the visitor tells apart. */
const timeZoneOption = (timeZone: string) => ({
  value: timeZone,
  label: `${timeZoneLabel(timeZone)} (${cityOf(timeZone)})`,
});

// The office list is a module constant, so its options are built once rather than per render.
const officeTimeZoneOptions = usTimeZones.map(timeZoneOption);

/**
 * Zones offered in the picker. The US list the office settings already carry doubles as a
 * sensible menu for a US visitor; a zone resolved from a ZIP that is not on it is prepended so
 * the select never holds a value it does not list.
 */
export function listTimeZoneOptions(current: string) {
  return officeTimeZoneOptions.some((option) => option.value === current)
    ? officeTimeZoneOptions
    : [timeZoneOption(current), ...officeTimeZoneOptions];
}
